import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import TextInput from 'components/ui/TextInput';
import { ReactComponent as SuccessIcon } from 'images/Success.svg';
import { APPLY_DISCOUNT_CODE, ROLLBACK_DISCOUNT_CODE_REDEMPTION } from 'store/mutations';

import {
  ErrorMessage,
  StyledConfirmButton,
  StyledCancelButton,
  AppliedDiscountContainer,
  AppliedDiscountCodeContainer,
  DiscountFormContent,
  SuccessIconWrapper
} from './DiscountCodeForm.styles';

const DiscountCodeForm = ({ bookingReference, appliedDiscounts = [] }) => {
  const [errorMessage, setErrorMessage] = useState(null);

  const { register, handleSubmit, formState, watch } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;

  const discountCodeValue = watch('discountCode');

  useEffect(() => {
    setErrorMessage(null);
  }, [setErrorMessage, discountCodeValue]);

  const [applyDiscountCode, { loading }] = useMutation(APPLY_DISCOUNT_CODE, {
    refetchQueries: ['booking']
  });

  const [rollbackVoucherCodeRedemption, { loading: rollbackLoading }] = useMutation(ROLLBACK_DISCOUNT_CODE_REDEMPTION, {
    refetchQueries: ['booking']
  });

  const handleOnSubmit = async (formData) => {
    try {
      await applyDiscountCode({
        variables: {
          bookingReference,
          discountCode: formData.discountCode
        }
      });
    } catch (err) {
      const error = err.graphQLErrors?.[0];
      if (error?.code === 'BAD_USER_INPUT') {
        setErrorMessage('discountCode.validationError');
      } else if (error?.message && error?.code === 'discount_booking_currency_mismatch') {
        setErrorMessage('discountCode.currencyMismatch');
      } else if (error?.message && error?.code !== 'INTERNAL_SERVER_ERROR') {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('discountCode.serverError');
      }
    }
  };

  const removeDiscountCode = async (discountCode) => {
    rollbackVoucherCodeRedemption({
      variables: {
        bookingReference,
        discountCode
      }
    });
  };

  return (
    <form id="apply-discount-code-form" onSubmit={handleSubmit(handleOnSubmit)}>
      {appliedDiscounts.length > 0 && (
        <>
          {appliedDiscounts.map((discount) => (
            <DiscountFormContent key={discount.code}>
              <AppliedDiscountContainer>
                <AppliedDiscountCodeContainer>
                  <SuccessIconWrapper>
                    <SuccessIcon />
                  </SuccessIconWrapper>
                  <strong data-testid="applied-discount-code">{discount.code}</strong>
                </AppliedDiscountCodeContainer>
                <StyledCancelButton
                  data-cy="remove-discount-code-btn"
                  data-testid="remove"
                  loading={rollbackLoading}
                  disabled={loading || rollbackLoading}
                  onClick={() => removeDiscountCode(discount.code)}
                >
                  <FormattedMessage id="discountCode.remove" />
                </StyledCancelButton>
              </AppliedDiscountContainer>
            </DiscountFormContent>
          ))}
        </>
      )}
      {appliedDiscounts.length === 0 && (
        <DiscountFormContent>
          <TextInput
            type="text"
            data-cy="discount-code"
            data-testid="discount-code"
            maxLength={50}
            {...register('discountCode', {
              required: true,
              minLength: 3,
              maxLength: 50
            })}
          />
          <StyledConfirmButton
            type="submit"
            data-cy="apply-discount-code-btn"
            data-testid="apply"
            loading={loading}
            disabled={loading || rollbackLoading || !formState.isValid}
          >
            <FormattedMessage id="discountCode.apply" />
          </StyledConfirmButton>
        </DiscountFormContent>
      )}
      {errors.discountCode && (
        <ErrorMessage id="error-discount-code">
          <FormattedMessage id="discountCode.invalidFormatError" />
        </ErrorMessage>
      )}
      {!errors.discountCode && errorMessage && (
        <ErrorMessage id="error-discount-code">
          <FormattedMessage id={errorMessage} />
        </ErrorMessage>
      )}
    </form>
  );
};

DiscountCodeForm.propTypes = {
  bookingReference: PropTypes.string,
  appliedDiscounts: PropTypes.arrayOf(PropTypes.object)
};

export default DiscountCodeForm;
